/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { useEffect, useState } from "react";
import * as locales from "date-fns/locale";
import { Locales, MMddyyyy, ddMMyyyy, yyyyMMddDashed } from "@advapacs/common";
import ReactDatePicker from "react-datepicker";
import { Portal } from "react-overlays";
import { getMonth, getYear } from "date-fns";
import MaskedInput from "react-text-mask";
import "react-datepicker/dist/react-datepicker.css";
import { CommonReactDatePickerProps } from "../types/components/datetime";
import { range } from "../utils/utils";

export const RenderCustomHeader = (
  locale: Locale,
  params: {
    monthDate: Date;
    date: Date;
    changeYear(year: number): void;
    changeMonth(month: number): void;
    customHeaderCount: number;
    decreaseMonth(): void;
    increaseMonth(): void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
    decreaseYear(): void;
    increaseYear(): void;
    prevYearButtonDisabled: boolean;
    nextYearButtonDisabled: boolean;
    isFilterOverCurrentYear?: boolean;
  },
  isDark?: boolean
): React.ReactNode => {
  const years = range(
    1900,
    getYear(new Date()) + (params.isFilterOverCurrentYear ? 10 : 0),
    1
  );
  const months = range(0, 11).map((m) => locale.localize?.month(m)) as string[];
  return (
    <div
      className={`datepicker-header datepicker-header__${
        isDark ? "dark" : "light"
      } `}
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className="btn btn-light"
        onClick={params.decreaseMonth}
        disabled={params.prevMonthButtonDisabled}
      >
        {"<"}
      </button>
      <select
        style={{ width: "84px" }}
        className="form-control datepicker-header"
        aria-readonly={false}
        defaultValue={getYear(params.date)}
        value={getYear(params.date)}
        onChange={({ target: { value } }) =>
          params.changeYear(parseInt(value, 10))
        }
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <select
        className="form-control datepicker-header"
        defaultValue={months[getMonth(params.date)]}
        aria-readonly={false}
        value={months[getMonth(params.date)]}
        onChange={({ target: { value } }) =>
          params.changeMonth(months.indexOf(value))
        }
      >
        {months.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="btn btn-light"
        onClick={params.increaseMonth}
        disabled={params.nextMonthButtonDisabled}
      >
        {">"}
      </button>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CalendarContainer = ({ children }: any) => {
  const el = document.getElementById("calendar-portal");

  return <Portal container={el}>{children}</Portal>;
};

export const CommonDatePicker = (
  props: CommonReactDatePickerProps
): React.ReactElement => {
  const {
    onChange,
    selected,
    minDate,
    maxDate,
    placeholderText,
    dateFormat,
    disabled,
    readOnly,
    placement = "bottom-start",
    isClearable = true,
    isDependPortal = true,
    localName = "enUS",
    unfold,
    showTimeInput,
    className = "",
    name = placeholderText,
    filterTime,
    isDark = false,
  } = props;
  const [dateState, setDateState] = useState<Date | undefined>(selected);
  const locale = (locales as Locales)[localName];
  const [isFocussed, setIsFocussed] = useState(false);
  useEffect(() => {
    setDateState(selected);
  }, [selected]);

  return (
    <>
      <ReactDatePicker
        fixedHeight
        popperClassName={`react-datepicker-popper__${
          isDark ? "dark" : "light"
        }`}
        name={name?.replace(" ", "").toLowerCase() || "datePicker"}
        onFocus={() => {
          setIsFocussed(true);
        }}
        onBlur={() => {
          setIsFocussed(false);
        }}
        disabled={disabled}
        placeholderText={
          isFocussed && dateFormat ? dateFormat : placeholderText
        }
        className={`form-control ${className} `}
        onChange={(change: Date) => {
          onChange(change);
          setDateState(change);
        }}
        customInput={
          [ddMMyyyy, MMddyyyy, yyyyMMddDashed].includes(
            dateFormat as string
          ) ? (
            <MaskedInput
              placeholder={dateFormat}
              mask={
                dateFormat === yyyyMMddDashed
                  ? [/\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/]
                  : [/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/]
              }
              keepCharPositions={true}
              guide={true}
            />
          ) : undefined
        }
        open={unfold ? false : undefined}
        showTimeInput={showTimeInput}
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        filterTime={(date) => {
          if (filterTime) {
            return filterTime(date);
          }
          return true;
        }}
        wrapperClassName="w-100"
        selected={dateState}
        popperPlacement={placement}
        dateFormat={dateFormat}
        renderCustomHeader={(params) =>
          RenderCustomHeader(
            locale,
            {
              ...params,
              isFilterOverCurrentYear: true,
            },
            isDark
          )
        }
        isClearable={isClearable && !disabled && !readOnly}
        readOnly={readOnly}
        showTimeSelect={dateFormat?.includes(":mm")}
        popperContainer={isDependPortal ? CalendarContainer : undefined}
        autoComplete="new-password"
      />
    </>
  );
};
